import React from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";

const Container = styled.div`
  ${tw`relative -mx-8 font-work_sans`}
  padding-top: clamp(2.5rem, 2rem + 2vw, 4rem);
`;

const TwoColumn = tw.div`flex flex-col lg:flex-row bg-[#FFFBDC] text-[#14133B] min-h-[60vh]`;

const LeftColumn = tw.div`px-8 xl:pl-12 pt-10 pb-16 lg:w-1/2 flex flex-col justify-center min-h-[50vh] lg:min-h-0`;

const RightColumn = styled.div(({ $bg }) => css`
  ${tw`w-full`}
  position: relative;
  flex: 1 1 auto;
  min-height: 50vh;
  background-color: #fffbdc;
  background-image: url("${$bg}");
  background-size: cover;
  background-position: center;

  @media (min-width: 1024px) {
    flex: none;
    width: 50%;
    min-height: auto;
    background-image: none;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6rem 0;
  }
`);

const ImageBox = styled.div(({ $bg }) => css`
  display: none;

  @media (min-width: 1024px) {
    display: block;
    width: 78%;
    aspect-ratio: 4 / 5;
    border-radius: 30px;
    overflow: hidden;
    background-image: url("${$bg}");
    background-size: cover;
    background-position: center;
    box-shadow: 0 32px 60px rgba(20, 19, 65, 0.16);
  }
`);

const Content = tw.div`mt-2 lg:mt-6 mb-6 flex flex-col items-start text-left`;

const Eyebrow = tw.span`text-xs uppercase tracking-[0.28em] text-[#14133B]`;

const Heading = styled.h1`
  ${tw`mt-4 text-3xl sm:text-5xl md:text-6xl tracking-tight uppercase`}
  line-height: 0.9;
  @media (min-width: 768px) {
    line-height: 1;
  }
`;

const Paragraph = styled.p`
  ${tw`mt-6 lg:mt-10 leading-snug text-base sm:text-lg`}
  max-width: 40rem;
`;

const SupportingText = styled.p`
  ${tw`mt-4 text-sm sm:text-base`}
  max-width: 34rem;
`;

const InfoList = tw.ul`mt-8 space-y-3 text-sm sm:text-base`;
const InfoItem = styled.li`
  ${tw`flex items-center gap-3`}

  &::before {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #14133b;
    opacity: 0.45;
    flex-shrink: 0;
  }
`;

const ButtonRow = tw.div`mt-10 flex flex-wrap items-center gap-4`;

const PrimaryButton = styled.button`
  ${tw`inline-flex items-center justify-center uppercase font-semibold`}
  padding: 0.85rem 2.6rem;
  border-radius: 999px;
  background: #14133b;
  color: #fffbdc;
  border: none;
  letter-spacing: 0.08em;
  cursor: pointer;

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SecondaryLink = styled.a`
  ${tw`inline-flex items-center justify-center uppercase font-semibold`}
  padding: 0.85rem 2.6rem;
  border-radius: 999px;
  border: 1.5px solid #14133b;
  background: rgba(20, 19, 59, 0.08);
  color: #14133b;
  text-decoration: none;
  letter-spacing: 0.08em;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const ContactIntro = ({
  onFormClick,
  email = "customerservice@grainm.com",
  rightImage = "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/lifestyle-photos/Grain_-279.jpg",
}) => {
  return (
    <Container>
      <TwoColumn>
        <LeftColumn>
          <Content>
            <Eyebrow>We would love to hear from you</Eyebrow>
            <Heading>
              Let&apos;s
              <br />
              connect.
            </Heading>

            <Paragraph>
              Whether you have a question about catering, partnerships, or just want to share feedback, we&apos;re all ears. Tell us what you need and our team will be in touch within one business day.
            </Paragraph>
            <SupportingText>
              Prefer a direct line? Reach out via phone or email, or tap the button below to send us a note straight from the site.
            </SupportingText>

            <InfoList>
              <InfoItem>
                <span>{email}</span>
              </InfoItem>
            </InfoList>

            <ButtonRow>
              <PrimaryButton onClick={() => onFormClick?.()}>
                Message us
              </PrimaryButton>
              <SecondaryLink href={`mailto:${email}`}>
                Email directly
              </SecondaryLink>
            </ButtonRow>
          </Content>
        </LeftColumn>

        <RightColumn $bg={rightImage}>
          <ImageBox $bg={rightImage} aria-hidden="true" />
        </RightColumn>
      </TwoColumn>
    </Container>
  );
};

export default ContactIntro;
